import JalaliDatePicker from "../../components/JalaliDatePicker";
import { useMemo, useState } from "react";

const COPY = {
  en: {
    title: "Cycle Management",
    subtitle: "See whether the cycle is on track, what remains, and when it is ready to close.",
    selectedCycle: "Cycle",
    newCycle: "New cycle",
    cycleName: "Cycle name",
    start: "Start",
    end: "End",
    create: "Create cycle",
    cancel: "Cancel",
    active: "Active",
    draft: "Draft",
    locked: "Closed · Locked",
    finalizing: "Finalizing",
    calendarProgress: "Calendar progress",
    evaluationProgress: "Evaluation completion",
    activities: "Evaluation activities",
    needsAttention: "Needs attention",
    noAttention: "Everything is progressing without outstanding items.",
    closing: "Cycle closing readiness",
    readyToClose: "Ready to finalize",
    notReady: "Outstanding items must be completed before finalizing.",
    activate: "Activate cycle",
    finalize: "Finalize & Lock Cycle",
    reopen: "Reopen cycle",
    completed: "completed",
    pending: "remaining",
    viewDetails: "View details",
    hideDetails: "Hide details",
    noRemaining: "No remaining items.",
    setup: "Cycle setup",
    setupHint: "This draft cycle is being prepared. Organization templates and model settings are managed outside Cycle Management.",
    datesReady: "Cycle dates defined",
    routineReady: "Core responsibility setup available",
    indicatorsReady: "Collective indicators configured",
    defaultsReady: "Organization defaults inherited",
    goals: "Individual Goals",
    routine: "Core Responsibilities",
    activitiesComp: "Additional Contributions",
    peer: "Peer Feedback",
    indicators: "Collective Indicators",
    managerReview: "Manager Review",
    goalAwaitingConfirmation: "Awaiting confirmation",
    goalIncomplete: "Not completed",
    activityAwaitingReview: "Awaiting review",
    peerReviewPendingFor: "Review pending for",
    indicatorAwaitingApproval: "Awaiting approval",
    indicatorDataMissing: "Data not submitted",
    routineIncomplete: "Not completed",
    managerReviewPending: "Manager review pending",
    finalizedOn: "Finalized",
    historyNote: "This cycle is locked and read-only.",
  },
  fr: {
    title: "Gestion du cycle",
    subtitle: "Vérifiez l’avancement du cycle, les éléments restants et son état de clôture.",
    selectedCycle: "Cycle",
    newCycle: "Nouveau cycle",
    cycleName: "Nom du cycle",
    start: "Début",
    end: "Fin",
    create: "Créer le cycle",
    cancel: "Annuler",
    active: "Actif",
    draft: "Brouillon",
    locked: "Clôturé · Verrouillé",
    finalizing: "Finalisation",
    calendarProgress: "Progression du calendrier",
    evaluationProgress: "Avancement de l’évaluation",
    activities: "Activités d’évaluation",
    needsAttention: "À surveiller",
    noAttention: "Tout progresse sans élément en attente.",
    closing: "Préparation à la clôture",
    readyToClose: "Prêt à finaliser",
    notReady: "Les éléments en attente doivent être complétés avant la finalisation.",
    activate: "Activer le cycle",
    finalize: "Finaliser et verrouiller",
    reopen: "Rouvrir le cycle",
    completed: "terminés",
    pending: "restants",
    viewDetails: "Voir les détails",
    hideDetails: "Masquer les détails",
    noRemaining: "Aucun élément restant.",
    setup: "Préparation du cycle",
    setupHint: "Ce cycle est en préparation. Les modèles et paramètres du modèle sont gérés en dehors de la Gestion du cycle.",
    datesReady: "Dates du cycle définies",
    routineReady: "Configuration des responsabilités disponible",
    indicatorsReady: "Indicateurs collectifs configurés",
    defaultsReady: "Paramètres par défaut hérités",
    goals: "Objectifs individuels",
    routine: "Responsabilités principales",
    activitiesComp: "Contributions additionnelles",
    peer: "Rétroaction des pairs",
    indicators: "Indicateurs collectifs",
    managerReview: "Évaluation du gestionnaire",
    goalAwaitingConfirmation: "En attente de confirmation",
    goalIncomplete: "Non terminé",
    activityAwaitingReview: "En attente de révision",
    peerReviewPendingFor: "Évaluation en attente pour",
    indicatorAwaitingApproval: "En attente d’approbation",
    indicatorDataMissing: "Données non soumises",
    routineIncomplete: "Non terminé",
    managerReviewPending: "Évaluation du gestionnaire en attente",
    finalizedOn: "Finalisé",
    historyNote: "Ce cycle est verrouillé et en lecture seule.",
  },
  fa: {
    title: "مدیریت دوره",
    subtitle: "در یک نگاه ببینید دوره چقدر پیش رفته، چه چیزهایی باقی مانده و چه زمانی آماده نهایی‌سازی است.",
    selectedCycle: "دوره",
    newCycle: "دوره جدید",
    cycleName: "نام دوره",
    start: "شروع",
    end: "پایان",
    create: "ایجاد دوره",
    cancel: "انصراف",
    active: "فعال",
    draft: "آماده‌سازی",
    locked: "نهایی‌شده · قفل‌شده",
    finalizing: "در حال نهایی‌سازی",
    calendarProgress: "پیشرفت زمانی دوره",
    evaluationProgress: "پیشرفت ارزیابی",
    activities: "فعالیت‌های ارزیابی",
    needsAttention: "نیازمند اقدام",
    noAttention: "همه بخش‌ها بدون مورد معوق در حال پیشرفت هستند.",
    closing: "آمادگی برای بستن دوره",
    readyToClose: "آماده نهایی‌سازی",
    notReady: "قبل از نهایی‌سازی باید موارد باقی‌مانده تکمیل شوند.",
    activate: "فعال‌سازی دوره",
    finalize: "نهایی‌سازی و قفل دوره",
    reopen: "بازگشایی دوره",
    completed: "تکمیل‌شده",
    pending: "باقی‌مانده",
    viewDetails: "مشاهده جزئیات",
    hideDetails: "بستن جزئیات",
    noRemaining: "مورد باقی‌مانده‌ای وجود ندارد.",
    setup: "آماده‌سازی دوره",
    setupHint: "این دوره در حال آماده‌سازی است. الگوها، وزن‌ها و تنظیمات مدل خارج از مدیریت دوره تعریف می‌شوند.",
    datesReady: "تاریخ شروع و پایان مشخص است",
    routineReady: "تنظیم وظایف مستمر موجود است",
    indicatorsReady: "شاخص‌های مشارکت جمعی تعریف شده‌اند",
    defaultsReady: "پیش‌فرض‌های سازمان اعمال شده‌اند",
    goals: "اهداف فردی",
    routine: "وظایف و مسئولیت‌های مستمر",
    activitiesComp: "فعالیت‌های غیرمستمر",
    peer: "بازخورد همکاران",
    indicators: "شاخص‌های مشارکت جمعی",
    managerReview: "ارزیابی مدیر",
    goalAwaitingConfirmation: "در انتظار تأیید",
    goalIncomplete: "تکمیل نشده",
    activityAwaitingReview: "در انتظار بررسی",
    peerReviewPendingFor: "بازخورد در انتظار برای",
    indicatorAwaitingApproval: "در انتظار تأیید",
    indicatorDataMissing: "داده ثبت نشده",
    routineIncomplete: "تکمیل نشده",
    managerReviewPending: "ارزیابی مدیر انجام نشده",
    finalizedOn: "نهایی‌شده در",
    historyNote: "این دوره قفل شده و فقط‌خواندنی است.",
  },
};

const card = { background: "#fff", border: "1px solid #e7e8ee", borderRadius: 14 };
const muted = "#717582";
const componentLabel = (copy, id) => id === "activities" ? copy.activitiesComp : (copy[id] || id);

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
function progressFor(period) {
  const start = normalizeDate(period?.startDate || period?.start);
  const end = normalizeDate(period?.endDate || period?.end);
  if (!start || !end || end <= start) return null;
  return Math.max(0, Math.min(100, Math.round(((new Date() - start) / (end - start)) * 100)));
}
function dateText(value, locale) {
  const date = normalizeDate(value);
  if (!date) return "—";
  try { return new Intl.DateTimeFormat(locale === "fa" ? "fa-IR" : locale === "fr" ? "fr-CA" : "en-CA", { year:"numeric", month:"short", day:"numeric" }).format(date); }
  catch { return String(value); }
}
function ProgressBar({ value }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  return <div style={{ height: 7, background: "#eef0f4", borderRadius: 999, overflow: "hidden" }}>
    <div style={{ width: `${safe}%`, height: "100%", background: "#000064", borderRadius: 999 }} />
  </div>;
}

export default function CycleManagement({
  locale = "en",
  periods = [],
  selectedPeriodId = "",
  onPeriodChange,
  cycleStatus = null,
  loading = false,
  onCreateCycle,
  onActivate,
  onFinalize,
  onReopen,
}) {
  const copy = COPY[locale] || COPY.en;
  const direction = locale === "fa" ? "rtl" : "ltr";
  const [expanded, setExpanded] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCycle, setNewCycle] = useState({ label:"", start:"", end:"" });
  const period = cycleStatus?.period || periods.find(p => p.id === selectedPeriodId) || null;
  const components = cycleStatus?.components || [];
  const closing = cycleStatus?.closing;
  const calendar = useMemo(() => progressFor(period), [period?.startDate, period?.endDate, period?.start, period?.end]);
  const status = String(period?.status || "").toLowerCase();
  const statusText = status === "active" ? copy.active : status === "locked" ? copy.locked : status === "finalizing" ? copy.finalizing : copy.draft;
  const overall = cycleStatus?.overallProgress ?? 0;
  const config = cycleStatus?.configuration || {};
  const attention = components.filter(item => item.pending > 0);

  const createCycle = async () => {
    if (!newCycle.label.trim() || !newCycle.start || !newCycle.end || !onCreateCycle) return;
    const created = await onCreateCycle(newCycle);
    if (created) {
      setNewCycle({ label:"", start:"", end:"" });
      setCreating(false);
    }
  };

  return <section dir={direction} style={{ maxWidth: 1180, margin: "0 auto" }}>
    <header style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 22, fontWeight: 760 }}>{copy.title}</div>
      <div style={{ marginTop: 5, fontSize: 12, color: muted, lineHeight: 1.7 }}>{copy.subtitle}</div>
    </header>

    <div style={{ ...card, padding: 13, marginBottom: 13 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", gap:12, flexWrap:"wrap" }}>
        <label style={{ minWidth: 240 }}>
          <span style={{ display:"block", fontSize:9.5, color:"#9295a0", marginBottom:4 }}>{copy.selectedCycle}</span>
          <select value={selectedPeriodId || ""} onChange={e => onPeriodChange?.(e.target.value)}
            style={{ width:"100%", padding:"9px 10px", border:"1px solid #dedfe6", borderRadius:9, background:"#fff", fontFamily:"inherit", fontSize:11.5 }}>
            {periods.map(p => <option key={p.id} value={p.id}>{p.label || p.name}{p.active ? (locale==="fa"?" — جاری":locale==="fr"?" — actuel":" — current") : ""}</option>)}
          </select>
        </label>
        <button type="button" className="cvi-button-secondary" onClick={() => setCreating(v => !v)}>+ {copy.newCycle}</button>
      </div>
      {creating && <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #ececf1", display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end" }}>
        <label><span style={{display:"block",fontSize:9.5,color:muted,marginBottom:4}}>{copy.cycleName}</span><input value={newCycle.label} onChange={e=>setNewCycle(x=>({...x,label:e.target.value}))} style={{padding:"8px 9px",border:"1px solid #dedfe6",borderRadius:8,fontFamily:"inherit"}} /></label>
        <label><span style={{display:"block",fontSize:9.5,color:muted,marginBottom:4}}>{copy.start}</span>{locale==="fa" ? <JalaliDatePicker value={newCycle.start} onChange={value=>setNewCycle(x=>({...x,start:value}))} /> : <input type="date" value={newCycle.start} onChange={e=>setNewCycle(x=>({...x,start:e.target.value}))} style={{padding:"8px 9px",border:"1px solid #dedfe6",borderRadius:8,fontFamily:"inherit"}} />}</label>
        <label><span style={{display:"block",fontSize:9.5,color:muted,marginBottom:4}}>{copy.end}</span>{locale==="fa" ? <JalaliDatePicker value={newCycle.end} onChange={value=>setNewCycle(x=>({...x,end:value}))} /> : <input type="date" value={newCycle.end} onChange={e=>setNewCycle(x=>({...x,end:e.target.value}))} style={{padding:"8px 9px",border:"1px solid #dedfe6",borderRadius:8,fontFamily:"inherit"}} />}</label>
        <button className="cvi-button-primary" onClick={createCycle}>{copy.create}</button>
        <button className="cvi-button-secondary" onClick={()=>setCreating(false)}>{copy.cancel}</button>
      </div>}
    </div>

    {!period ? <div style={{ ...card, padding:28, textAlign:"center", color:muted }}>{copy.newCycle}</div> : <>
      <div style={{ ...card, padding:18, marginBottom:13 }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:16, flexWrap:"wrap", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:18, fontWeight:760 }}>{period.name || periods.find(p=>p.id===selectedPeriodId)?.label || "—"}</div>
            <div style={{ marginTop:5, fontSize:10.8, color:muted }}>{dateText(period.startDate || period.start, locale)} – {dateText(period.endDate || period.end, locale)}</div>
            <div style={{ marginTop:7, display:"inline-flex", padding:"4px 8px", borderRadius:999, background:"#f2f3f6", fontSize:10.5, fontWeight:650 }}>{statusText}</div>
          </div>
          <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
            <div><div style={{fontSize:9.5,color:"#9295a0"}}>{copy.calendarProgress}</div><strong style={{fontSize:18}}>{calendar == null ? "—" : `${calendar}%`}</strong></div>
            <div><div style={{fontSize:9.5,color:"#9295a0"}}>{copy.evaluationProgress}</div><strong style={{fontSize:18}}>{loading ? "…" : `${overall}%`}</strong></div>
          </div>
        </div>
        <div style={{ marginTop:14 }}><ProgressBar value={overall} /></div>
      </div>

      {status !== "locked" && status !== "active" && <div style={{ ...card, padding:15, marginBottom:13 }}>
        <div style={{ fontSize:13.5, fontWeight:750 }}>{copy.setup}</div>
        <div style={{ marginTop:4, fontSize:10.8, lineHeight:1.8, color:muted }}>{copy.setupHint}</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:8, marginTop:12 }}>
          {[
            [copy.datesReady, Boolean(period.startDate || period.start) && Boolean(period.endDate || period.end)],
            [copy.routineReady, (config.routineQuestions || 0) > 0],
            [copy.indicatorsReady, (config.indicators || 0) > 0],
            [copy.defaultsReady, true],
          ].map(([text,ok]) => <div key={text} style={{padding:"9px 10px",borderRadius:9,background:"#fafafb",fontSize:11.2}}><span style={{marginInlineEnd:7}}>{ok?"✓":"○"}</span>{text}</div>)}
        </div>
        <div style={{ marginTop:12 }}><button className="cvi-button-primary" onClick={()=>onActivate?.(selectedPeriodId)}>{copy.activate}</button></div>
      </div>}

      <div style={{ marginBottom:13 }}>
        <div style={{ fontSize:13.5, fontWeight:750, marginBottom:9 }}>{copy.activities}</div>
        <div style={{ display:"grid", gap:9 }}>
          {components.map(item => {
            const isOpen = expanded === item.id;
            return <div key={item.id} style={card}>
              <div style={{ padding:"13px 14px", display:"grid", gridTemplateColumns:"minmax(180px,1fr) auto auto", gap:12, alignItems:"center" }}>
                <div>
                  <div style={{fontSize:12.5,fontWeight:700}}>{componentLabel(copy,item.id)}</div>
                  <div style={{fontSize:10.5,color:muted,marginTop:4}}>{item.total > 0 ? <><strong style={{color:"#4d5160"}}>{item.completed} / {item.total}</strong> {copy.completed}</> : "—"}</div>
                </div>
                <span style={{padding:"4px 8px",borderRadius:999,background:item.pending>0?"#f3f3f6":"#eaf5ef",fontSize:10.2,whiteSpace:"nowrap"}}>{item.pending} {copy.pending}</span>
                <button type="button" className="cvi-button-secondary" disabled={!item.pending} onClick={()=>setExpanded(isOpen?"":item.id)}>{isOpen?copy.hideDetails:copy.viewDetails}</button>
              </div>
              {isOpen && <div style={{borderTop:"1px solid #ececf1",padding:"8px 14px 12px"}}>
                {!item.remaining?.length ? <div style={{fontSize:10.8,color:muted}}>{copy.noRemaining}</div> :
                  item.remaining.map(row => <div key={row.id} style={{padding:"8px 0",borderBottom:"1px solid #f0f0f3",display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                    <div><div style={{fontSize:11.5,fontWeight:650}}>{row.label}</div>{row.subject && <div style={{fontSize:10.3,color:muted,marginTop:2}}>{row.subject}</div>}</div>
                    <div style={{fontSize:10.5,color:"#666a77"}}>{copy[row.detail] || row.detail}{row.detail==="peerReviewPendingFor" && row.subject ? ` · ${row.subject}` : ""}</div>
                  </div>)}
              </div>}
            </div>;
          })}
        </div>
      </div>

      <div style={{ ...card, padding:15, marginBottom:13 }}>
        <div style={{fontSize:13.5,fontWeight:750,marginBottom:9}}>{copy.needsAttention}</div>
        {!attention.length ? <div style={{fontSize:11,color:muted}}>✓ {copy.noAttention}</div> :
          <div style={{display:"grid",gap:7}}>{attention.map(item => <div key={item.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"8px 10px",background:"#fafafb",borderRadius:9}}>
            <span style={{fontSize:11.5,fontWeight:600}}>{componentLabel(copy,item.id)}</span><span style={{fontSize:10.5,color:muted}}>{item.pending} {copy.pending}</span>
          </div>)}</div>}
      </div>

      <div style={{ ...card, padding:15 }}>
        <div style={{fontSize:13.5,fontWeight:750}}>{copy.closing}</div>
        {status === "locked" ? <div style={{marginTop:9}}>
          <div style={{fontSize:11,color:muted}}>{copy.historyNote}</div>
          {period.closedAt && <div style={{fontSize:10.5,color:muted,marginTop:4}}>{copy.finalizedOn}: {dateText(period.closedAt, locale)}</div>}
          <button className="cvi-button-secondary" style={{marginTop:11}} onClick={()=>onReopen?.(selectedPeriodId)}>{copy.reopen}</button>
        </div> : <>
          <div style={{display:"grid",gap:6,marginTop:10}}>
            {(closing?.checks || []).map(check => <div key={check.id} style={{fontSize:11.2}}><span style={{marginInlineEnd:7}}>{check.complete?"✓":"○"}</span>{componentLabel(copy,check.id)}</div>)}
          </div>
          <div style={{marginTop:10,fontSize:10.8,color:muted}}>{closing?.ready ? copy.readyToClose : copy.notReady}</div>
          {status === "active" && <button className="cvi-button-primary" disabled={!closing?.ready} style={{marginTop:11}} onClick={()=>onFinalize?.(selectedPeriodId)}>{copy.finalize}</button>}
        </>}
      </div>
    </>}
  </section>;
}
